import { useState } from 'react';
import { useLanguage } from '@/contexts/AppContext';
import type { CartItem } from '@/components/CartModal';

interface MenuItemsListProps {
  cartItems: CartItem[];
  formatCurrency: (amount: number) => string;
  t: {
    menu: string;
    qty: string;
    price: string;
  };
}

/**
 * 결제 화면의 메뉴 아이템 목록을 표시하는 컴포넌트
 * - Desktop Header (grid layout)
 * - Items List (cartItems.map)
 * - Mobile/Desktop Layout 지원
 * - expandedItems 상태로 커스터마이징 옵션 확장/축소 관리
 */
export function MenuItemsList({
  cartItems,
  formatCurrency,
  t
}: MenuItemsListProps) {
  const { language } = useLanguage();
  // 확장된 아이템 ID 배열 (모바일에서 커스터마이징 옵션 더보기)
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // 아이템 확장/축소 토글
  const toggleItemExpansion = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  return (
    <div className="bg-background rounded-[5px] px-[16px] py-[0px]">
      {/* Desktop Header - Hidden on mobile */}
      <div className="hidden sm:grid grid-cols-12 gap-3 pb-3 mb-4 border-b border-border">
        <div className="col-span-6 lg:col-span-7 text-xs text-muted-foreground uppercase tracking-wide">
          {t.menu}
        </div>
        <div className="col-span-2 lg:col-span-1 text-xs text-muted-foreground text-center uppercase tracking-wide">
          {t.qty}
        </div>
        <div className="col-span-4 text-xs text-muted-foreground text-right uppercase tracking-wide">
          {t.price}
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-4">
        {cartItems.map((item, index) => (
          <div key={item.id}>
            {/* Desktop Layout */}
            <div className="hidden sm:grid grid-cols-12 gap-3 items-start">
              {/* Menu Item */}
              <div className="col-span-6 lg:col-span-7">
                <div className="flex items-start gap-3">
                  <span className="text-xl flex-shrink-0 mt-0.5">&#x1F9EA;</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground mb-1.5">
                      {item.name}
                    </p>
                    {item.customizations && item.customizations.length > 0 && (
                      <div className="space-y-0.5">
                        {item.customizations.slice(0, 2).map((custom, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground leading-relaxed">
                            &bull; {custom}
                          </p>
                        ))}
                        {item.customizations.length > 2 && (
                          <p className="text-xs text-secondary mt-1">
                            +{item.customizations.length - 2} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div className="col-span-2 lg:col-span-1 flex justify-center">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-foreground">
                    {item.quantity}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="col-span-4 flex justify-end">
                <p className="text-sm font-medium text-foreground">
                  {formatCurrency(item.price * item.quantity)}
                </p>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="sm:hidden">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-2">
                    {item.name}
                  </p>

                  {/* Quantity and Price Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{t.qty}:</span>
                      <div className="w-7 h-7 bg-muted rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-foreground">
                          {item.quantity}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>

                  {/* Customizations */}
                  {item.customizations && item.customizations.length > 0 && (
                    <div className="space-y-0.5 pl-0 border-l-2 border-primary pl-2">
                      {(expandedItems.includes(item.id)
                        ? item.customizations
                        : item.customizations.slice(0, 1)
                      ).map((custom, idx) => (
                        <p key={idx} className="text-xs text-muted-foreground leading-relaxed">
                          &bull; {custom}
                        </p>
                      ))}
                      {item.customizations.length > 1 && (
                        <p
                          onClick={() => toggleItemExpansion(item.id)}
                          className="text-xs text-secondary mt-1 cursor-pointer hover:text-secondary/80 transition-colors"
                        >
                          {expandedItems.includes(item.id)
                            ? (language === 'ko' ? '접기' : language === 'es' ? 'Ocultar' : 'Hide')
                            : `+${item.customizations.length - 1} ${language === 'ko' ? '개 더보기' : language === 'es' ? 'mas opciones' : 'more options'}`
                          }
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Divider - Not on last item */}
            {index < cartItems.length - 1 && (
              <div className="h-px bg-border mt-4" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
